import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { connectSocket } from '../services/socket';
import { fetchNotifications } from '../redux/actions/notifications';
import { addNotification } from '../redux/reducers/notifications';
import { showBrowserNotification } from '../services/firebase';
import { NAV_NOTIFICATION_TYPES, NOTIFICATION_FILTERS } from '../common/notificationTypes';

/**
 * Alimenta el centro de notificaciones del nav a nivel de aplicación:
 * carga el historial al entrar y escucha `notification_new` mientras el
 * usuario esté autenticado, sin importar en qué pantalla esté.
 *
 * El servidor emite a la sala `user-<id>`, a la que el socket entra al
 * conectarse, así que no hace falta estar dentro de un curso.
 */
export const useNotificationsFeed = () => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user.user);
    const userId = user?._id;

    // Las preferencias se leen al recibir el evento, no al suscribirse.
    const preferencesRef = useRef(user?.notifications);
    preferencesRef.current = user?.notifications;

    useEffect(() => {
        if (!userId) return undefined;

        let mounted = true;
        let detach = null;

        dispatch(fetchNotifications({ filter: NOTIFICATION_FILTERS.unread })).catch(() => {
            // sin historial: el badge se llena con los eventos del socket
        });

        const init = async () => {
            try {
                const socket = await connectSocket();
                if (!mounted || !socket) return;

                const onNotificationNew = (notification) => {
                    if (!mounted || !notification) return;
                    // La lista paginada solo trae estos tipos; el socket debe coincidir.
                    if (!NAV_NOTIFICATION_TYPES.includes(notification.type)) return;

                    dispatch(addNotification(notification));

                    const preferences = preferencesRef.current;
                    if (preferences?.enabled === false) return;

                    showBrowserNotification(notification.title, {
                        body: notification.body,
                        userPreferences: preferences,
                        data: notification.data,
                        tag: notification._id,
                        onClick: () => {
                            if (notification.data?.courseId) {
                                const tab = notification.type === 'class_live' ? '?tab=classes' : '';
                                window.location.href = `/curso/${notification.data.courseId}${tab}`;
                            }
                        },
                    });
                };

                socket.on('notification_new', onNotificationNew);
                detach = () => {
                    try {
                        socket?.off?.('notification_new', onNotificationNew);
                    } catch {
                        // el socket ya se cerró
                    }
                };
            } catch (err) {
                console.warn('[Notifications] socket:', err?.message || err);
            }
        };

        init();

        return () => {
            mounted = false;
            if (detach) detach();
        };
    }, [userId, dispatch]);
};
